import { useState } from 'react';
import { ChevronDown, ChevronRight, Code2 } from 'lucide-react';
import type { TurnAudit } from '../hooks/useDebugSession';

function formatNumber(n: number): string {
  return n.toLocaleString('pt-BR');
}

function versionLabel(prefix: string, v: number | null | undefined): string | null {
  if (v == null) return null;
  return `${prefix} v${v}`;
}

export function DebugTurnCard({ audit }: { audit: TurnAudit }) {
  const [showSystem, setShowSystem] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const versionBadges: string[] = [];
  const modeLabel = versionLabel('mode', audit.modePromptVersion);
  const appendLabel = versionLabel('append', audit.commonAppendVersion);
  const contractLabel = versionLabel('contract', audit.toolContractVersion);
  if (modeLabel) versionBadges.push(modeLabel);
  if (appendLabel) versionBadges.push(appendLabel);
  if (contractLabel) versionBadges.push(contractLabel);

  return (
    <div className="my-3 mx-2 card border-l-2 border-l-blue-500 bg-blue-50/50">
      <div className="flex items-center gap-2 text-xs">
        <Code2 size={12} className="text-blue-600" />
        <span className="font-medium text-blue-700">
          Turn #{audit.turnNumber} · {audit.provider}/{audit.model}
        </span>
        <span className="ml-auto text-text-muted">
          ${audit.costUsd.toFixed(4)} · {formatNumber(audit.inputTokens)}↑ /{' '}
          {formatNumber(audit.outputTokens)}↓
          {audit.cachedTokens > 0 && ` · cached ${formatNumber(audit.cachedTokens)}`}
        </span>
      </div>

      {audit.toolsUsed.length > 0 && (
        <div className="mt-1 text-xs text-text-muted">
          Tools: {audit.toolsUsed.join(', ')}
        </div>
      )}

      {versionBadges.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
          {versionBadges.map((b) => (
            <span
              key={b}
              className="px-1.5 py-0.5 rounded border bg-green-100 text-green-800 border-green-300"
              title="Versão da row em /admin/prompts usada neste turno"
            >
              {b}
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowSystem((v) => !v)}
        className="mt-2 flex items-center gap-1 text-xs text-text-muted hover:text-text"
      >
        {showSystem ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        System prompt {audit.systemPromptTruncated && '(truncado)'}
      </button>
      {showSystem && (
        <pre className="mt-1 text-xs bg-bg p-2 rounded overflow-x-auto max-h-80 whitespace-pre-wrap break-words">
          {audit.systemPrompt}
        </pre>
      )}

      <button
        type="button"
        onClick={() => setShowHistory((v) => !v)}
        className="mt-2 flex items-center gap-1 text-xs text-text-muted hover:text-text"
      >
        {showHistory ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Histórico enviado ({audit.history.length} mensagens)
      </button>
      {showHistory && (
        <div className="mt-1 max-h-96 overflow-y-auto bg-bg p-2 rounded space-y-2">
          {audit.history.map((h, i) => (
            <div key={i} className="text-xs">
              <div className="font-mono text-text-muted">
                [{i}] {h.role} ({h.contentType}){h.truncated && ' truncado'}
              </div>
              <pre className="mt-0.5 whitespace-pre-wrap break-words pl-3">
                {h.text}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
